import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { api } from "./client";
import { ep } from "./endpoints";
import type {
  AppAccessMember,
  AppAdmitInviteCreated,
  Attachment,
  AttachmentWrite,
  Facets,
  Me,
  Page,
  Piece,
  PieceRow,
  PieceWrite,
  PriceBoard,
} from "./types";
import { filtersToQuery, type Filters } from "../features/filters/filterState";
import { forget as forgetMedia } from "../features/media/mediaCache";

/**
 * How stale a record is allowed to look. `gcTime` in App.tsx must stay well
 * under the signed-url lifetime, or a record resurrected from the query cache
 * renders urls whose tokens are already dead.
 */
const CATALOG_STALE = 5 * 60_000;

/** Used until the first board says what the server's TTL actually is. */
const PRICE_STALE_FALLBACK = 5 * 60_000;

export const keys = {
  me: ["me"] as const,
  facets: ["facets"] as const,
  prices: ["prices"] as const,
  pieces: (filters: Filters) => ["pieces", filtersToQuery(filters)] as const,
  piece: (id: string) => ["piece", id] as const,
};

/* -------------------------------------------------------------- identity -- */

/** The handshake. Proves the Zardir initData before anything else renders. */
export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: () => api.get<Me>(ep.me),
    staleTime: Infinity,
    retry: (count, error) =>
      // A rejected initData will never become valid by retrying.
      count < 2 && !("status" in error && [401, 403].includes(Number(error.status))),
  });
}

export function useAccessMembers(enabled = true) {
  return useQuery({
    queryKey: ["access", "members"] as const,
    queryFn: () => api.get<AppAccessMember[]>(ep.accessMembers),
    enabled,
    staleTime: CATALOG_STALE,
  });
}

export function useAddAccessMember() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { username?: string; telegram_id?: number; role: "admin" | "user" }) =>
      api.post<AppAccessMember>(ep.accessMembers, body),
    onSuccess: () => client.invalidateQueries({ queryKey: ["access", "members"] }),
  });
}

export function useRemoveAccessMember() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (telegramId: number) => api.delete(ep.accessMember(telegramId)),
    onSuccess: () => client.invalidateQueries({ queryKey: ["access", "members"] }),
  });
}

export function useCreateAccessInvite() {
  return useMutation({
    mutationFn: (kind: "admin" | "admit") =>
      api.post<AppAdmitInviteCreated>(ep.accessInvites, { kind }),
  });
}

export function useFacets() {
  return useQuery({
    queryKey: keys.facets,
    queryFn: () => api.get<Facets>(ep.facets),
    staleTime: 60_000,
  });
}

/* ---------------------------------------------------------------- prices -- */

/**
 * The market board. Fetched once when the app opens, then reused for as long
 * as the server says a board stays fresh; no polling, no refetch on focus. The
 * server itself only calls upstream when its cached board has aged out.
 */
export function usePrices(enabled = true) {
  return useQuery({
    queryKey: keys.prices,
    enabled,
    queryFn: ({ signal }) => api.get<PriceBoard>(ep.prices, undefined, signal),
    staleTime: (query) => (query.state.data?.ttl_seconds ?? 0) * 1000 || PRICE_STALE_FALLBACK,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,
  });
}

/** The refresh button: asks the server to skip its cached board, not just re-read it. */
export function useRefreshPrices() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.get<PriceBoard>(ep.prices, { refresh: "1" }),
    onSuccess: (board) => client.setQueryData(keys.prices, board),
  });
}

/* ---------------------------------------------------------------- pieces -- */

/** Reads the opaque cursor back out of the `next` URL DRF hands us. */
function cursorOf(url: string | null): string | null {
  if (!url) return null;
  try {
    return new URL(url, window.location.origin).searchParams.get("cursor");
  } catch {
    return null;
  }
}

export function usePieces(filters: Filters) {
  return useInfiniteQuery({
    queryKey: keys.pieces(filters),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      api.get<Page<PieceRow>>(
        ep.pieces,
        { ...filtersToQuery(filters), cursor: pageParam ?? undefined },
        signal,
      ),
    getNextPageParam: (last) => cursorOf(last.next),
    staleTime: CATALOG_STALE,
  });
}

export function usePiece(id: string | undefined) {
  return useQuery({
    queryKey: keys.piece(id ?? ""),
    queryFn: () => api.get<Piece>(ep.piece(id!)),
    enabled: Boolean(id),
    staleTime: CATALOG_STALE,
    refetchOnWindowFocus: true,
  });
}

/** Everything a write touches: the list, the counts, and the record itself. */
function invalidateCatalog(client: QueryClient, id?: string) {
  client.invalidateQueries({ queryKey: ["pieces"] });
  client.invalidateQueries({ queryKey: keys.facets });
  if (id) client.invalidateQueries({ queryKey: keys.piece(id) });
}

export function useCreatePiece() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: PieceWrite) => api.post<Piece>(ep.pieces, body),
    onSuccess: (piece) => {
      client.setQueryData(keys.piece(piece.id), piece);
      invalidateCatalog(client);
    },
  });
}

export function useUpdatePiece(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: PieceWrite) => api.patch<Piece>(ep.piece(id), body),
    onSuccess: (piece) => {
      client.setQueryData(keys.piece(id), piece);
      invalidateCatalog(client, id);
    },
  });
}

/** Soft delete on the server, so the Undo in the toast is a real restore. */
export function useDeletePiece() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(ep.piece(id)),
    onSuccess: (_data, id) => {
      // A soft-deleted piece 404s; refetching it would only log an error.
      client.removeQueries({ queryKey: keys.piece(id) });
      invalidateCatalog(client);
    },
  });
}

export function useRestorePiece() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Piece>(ep.restorePiece(id)),
    onSuccess: (piece) => {
      client.setQueryData(keys.piece(piece.id), piece);
      invalidateCatalog(client);
    },
  });
}

/* ------------------------------------------------------------ public page -- */

export function useSetPublic() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, value }: { id: string; value: boolean }) =>
      api.post<Piece>(ep.setPublic(id), { is_public: value }),
    onSuccess: (piece) => client.setQueryData(keys.piece(piece.id), piece),
  });
}

/** A new code orphans every tag printed with the old one. */
export function useRotateCode() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Piece>(ep.rotateCode(id)),
    onSuccess: (piece) => client.setQueryData(keys.piece(piece.id), piece),
  });
}

/** The bot drops a print-ready PNG into the owner's chat; WebViews ignore downloads. */
export function useSendQr() {
  return useMutation({
    mutationFn: (id: string) => api.post<void>(ep.sendQr(id)),
  });
}

/* ----------------------------------------------------------- attachments -- */

/** One hero per piece: confirming a `primary` evicts the previous one. */
function mergeAttachment(current: Attachment[], next: Attachment): Attachment[] {
  const kept = current.filter(
    (file) => file.id !== next.id && !(next.kind === "primary" && file.kind === "primary"),
  );
  return [...kept, next];
}

/**
 * Where a finished upload lands. Uploads bypass the mutation layer, so the
 * confirm response is written into the cached piece directly and the batch
 * reconciles the list and counts once it is done.
 */
export function useUploadedMedia() {
  const client = useQueryClient();
  return {
    absorb: (pieceId: string, attachment: Attachment) => {
      void client.cancelQueries({ queryKey: keys.piece(pieceId) });
      client.setQueryData<Piece>(keys.piece(pieceId), (old) =>
        old ? { ...old, attachments: mergeAttachment(old.attachments, attachment) } : old,
      );
    },
    settle: async (pieceId: string) => {
      invalidateCatalog(client, pieceId);
      await client.refetchQueries({ queryKey: keys.piece(pieceId) });
    },
  };
}

export function useUpdateAttachment(pieceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string } & AttachmentWrite) =>
      api.patch<Attachment>(ep.attachment(id), patch),
    onSuccess: () => invalidateCatalog(client, pieceId),
  });
}

export function useDeleteAttachment(pieceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(ep.attachment(id)),
    onSuccess: (_data, id) => {
      void forgetMedia(id);
      invalidateCatalog(client, pieceId);
    },
  });
}

export function useMakePrimary(pieceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Attachment>(ep.makePrimary(id)),
    onSuccess: (promoted) => {
      // Apply kinds now so the hero and the list thumbnail never disagree.
      client.setQueryData<Piece>(keys.piece(pieceId), (old) => {
        if (!old) return old;
        let saw = false;
        const attachments = old.attachments.map((file) => {
          if (file.id === promoted.id) {
            saw = true;
            return { ...file, ...promoted };
          }
          if (file.kind === "primary") return { ...file, kind: "gallery" as const };
          return file;
        });
        if (!saw) attachments.push(promoted);
        return { ...old, attachments, primary_media: promoted };
      });
      invalidateCatalog(client, pieceId);
    },
  });
}
